import { defineComponent } from "vue";
import Tuple from "@common/vue/Tuple.vue";
import { liveStore } from "../../../store/LiveStore";
import { metrePerSecondToKnot, metresToFeet, radToDeg } from "@common/utils/UnitConverter";
import FlightProgress from "../../misc/FlightProgress.vue";
import StdLineChart from "../../chart/line/StdLineChart.vue";
import StateSvg from "@common/vue/StateSvg.vue";
import { AUTO_REFRESH_FLIGHT_OVERLAY_MS } from "../../../LiveParams";
import { useToast } from "vue-toastification";
import fileDownload from "js-file-download";
import { FlightApi } from "@live/api/flight/FlightApi";
import { unixToHourMinuteZulu } from "@common/utils/Time";
import { FLICK_TARGET_SWITCH } from "@live/store/LiveMutations";
import { UPDATE_CURRENT_FLIGHT_DETAIL } from "@live/store/LiveActions";
export default defineComponent({
    components: {
        Tuple,
        FlightProgress,
        StdLineChart,
        StateSvg
    },
    computed: {
        flightDetail: function () {
            const flightDetail = liveStore.getters.getCurrentFlightDetail;
            return flightDetail;
        },
        callsign: function () {
            const flight = this.flightDetail;
            return flight == null ? "Unknown Callsign" : flight.callsign;
        },
        aircraftName: function () {
            const flight = this.flightDetail;
            return flight == null ? "Unknown Aircraft" : flight.aircraftName;
        },
        liveryName: function () {
            const flight = this.flightDetail;
            return flight == null ? "Unknown Livery" : flight.liveryName;
        },
        imageSrc: function () {
            const flight = this.flightDetail;
            return flight == null ? "img/404.webp" : flight.imageSrc;
        },
        speed: function () {
            const flight = this.flightDetail;
            return flight == null ? 0 : Math.round(metrePerSecondToKnot(flight.speed));
        },
        altitude: function () {
            const flight = this.flightDetail;
            return flight == null ? 0 : Math.round(metresToFeet(flight.altitude));
        },
        track: function () {
            const flight = liveStore.getters.getCurrentFlightSimple;
            return flight == null ? 0 : Math.round(radToDeg(flight.psi));
        },
        icaoDeparture: function () {
            const flight = this.flightDetail;
            return flight == null ? "????" : flight.departureIcao;
        },
        icaoDestination: function () {
            const flight = this.flightDetail;
            return flight == null ? "????" : flight.arrivalIcao;
        },
        departureTime: function () {
            const flight = this.flightDetail;
            if (flight == null)
                return "??h??Z";
            const departureUnix = flight.departureUnix;
            if (departureUnix == null)
                return "??h??Z";
            return unixToHourMinuteZulu(departureUnix);
        },
        arrivalTime: function () {
            const flight = this.flightDetail;
            if (flight == null)
                return "??h??Z";
            const arrivalUnix = flight.arrivalUnix;
            if (arrivalUnix == null)
                return "??h??Z";
            return unixToHourMinuteZulu(arrivalUnix);
        },
        flightProgress: function () {
            const flight = this.flightDetail;
            return flight == null ? 50 : flight.flightProgressPercentage;
        },
        fpl: function () {
            const flight = this.flightDetail;
            return flight == null || flight.fpl.trim() == "" ? "NO FLIGHT PLAN" : flight.fpl;
        },
        username: function () {
            const flight = this.flightDetail;
            return flight == null || flight.username.trim() == "" ? "No Username" : flight.username.trim();
        },
        userTitle: function () {
            const flight = this.flightDetail;
            return flight == null ? "Pilot" : flight.userTitle;
        },
        grade: function () {
            const flight = this.flightDetail;
            return flight == null ? 0 : flight.grade;
        },
        xp: function () {
            const flight = this.flightDetail;
            return flight == null ? 0 : flight.xp;
        },
        virtualGroup: function () {
            const flight = this.flightDetail;
            return flight == null ? "None" : flight.virtualGroup;
        },
        flightTime: function () {
            const flight = this.flightDetail;
            if (flight == null)
                return "??h??";
            const hours = `${Math.floor(flight.flightTime / 3600)}`.padStart(2, '0');
            const minutes = `${Math.floor(flight.flightTime / 60) % 60}`.padStart(2, '0');
            return `${hours}h${minutes}`;
        },
        atcOps: function () {
            const flight = this.flightDetail;
            return flight == null ? 0 : flight.atcOperations;
        },
        landings: function () {
            const flight = this.flightDetail;
            return flight == null ? 0 : flight.landings;
        },
        violationTotal: function () {
            const flight = this.flightDetail;
            return flight == null ? 0 : flight.violationTotal;
        },
        violation1: function () {
            const flight = this.flightDetail;
            return flight == null ? 0 : flight.violation1;
        },
        violation2: function () {
            const flight = this.flightDetail;
            return flight == null ? 0 : flight.violation2;
        },
        violation3: function () {
            const flight = this.flightDetail;
            return flight == null ? 0 : flight.violation3;
        },
        altitudes: function () {
            const flight = this.flightDetail;
            if (flight == null) {
                return { labels: [""], values: [0] };
            }
            return {
                labels: flight.route.map((value) => unixToHourMinuteZulu(value.unix)),
                values: flight.route.map((value) => metresToFeet(value.altitude))
            };
        },
        speeds: function () {
            const flight = this.flightDetail;
            if (flight == null) {
                return { labels: [""], values: [0] };
            }
            return {
                labels: flight.route.map((value) => unixToHourMinuteZulu(value.unix)),
                values: flight.route.map((value) => metrePerSecondToKnot(value.speed))
            };
        },
        lastUpdated: function () {
            const flight = this.flightDetail;
            if (flight == null) {
                return "Unknown";
            }
            const date = new Date(flight.lastReportUnix * 1000);
            return date.toUTCString();
        },
    },
    data: function () {
        return {
            flightRefreshIntervalId: null
        };
    },
    methods: {
        onClickShare: function () {
            navigator.clipboard.writeText(`${window.location.origin}${this.$route.fullPath}`);
            this.toast.info("Copied Flight URL!");
        },
        onClickDownload: async function () {
            const server = liveStore.getters.getCurrentServerSimple;
            const flight = liveStore.getters.getCurrentFlightSimple;
            if (server == null || flight == null)
                return;
            const kml = await FlightApi.getFlightKml(server.id, flight.id);
            fileDownload(kml, `flight_${flight.id}.kml`);
        },
        onClickTarget: function () {
            liveStore.commit(FLICK_TARGET_SWITCH);
            this.$emit("onClose");
        }
    },
    setup: function () {
        const toast = useToast();
        return { toast };
    },
    mounted: function () {
        this.flightRefreshIntervalId = window.setInterval(() => {
            liveStore.dispatch(UPDATE_CURRENT_FLIGHT_DETAIL);
        }, AUTO_REFRESH_FLIGHT_OVERLAY_MS);
    },
    unmounted: function () {
        if (this.flightRefreshIntervalId != null) {
            window.clearInterval(this.flightRefreshIntervalId);
            this.flightRefreshIntervalId = null;
        }
    },
    emits: ["onClose"]
});
